import React, { FC, useEffect, useState } from 'react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { Icon } from '@epoch-ui/components/Icon';
import SelectionButton from 'components/common/buttons/SelectionButton';
import { DESCRIPTION_EVENT_TYPE } from 'constants/ai-prompt.constants';

import AIBetaPill from '../../AIBetaPill';

import EventDescriptionAISteps from './EventDescriptionAISteps';

interface EventTypePromptModalProps extends React.ComponentProps<typeof Modal> {
  handleSubmit: (types: string[], other?: string) => void;
  selectedTypes?: string[];
  otherInput?: string;
}

/**
 * AI modal prompts for Event description.
 * step 1: Ask the type of event.
 * @param param0
 * @returns
 */
const EventTypePromptModal: FC<React.PropsWithChildren<EventTypePromptModalProps>> = ({
  handleSubmit,
  selectedTypes,
  otherInput,
  open: isOpen,
  onOpenChange: onRequestClose,
  ...props
}) => {
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [other, setOther] = useState<string>();

  useEffect(() => {
    setEventTypes(selectedTypes || []);
    setOther(otherInput || '');
  }, [selectedTypes]);

  return (
    <Modal open={isOpen} onOpenChange={(open) => !open && onRequestClose?.(false)} {...props}>
      <ModalContent size="md" className="tw-max-w-[556px]">
        <ModalHeader>
          <div className="tw-flex tw-flex-col tw-items-start">
            <AIBetaPill />
            <ModalTitle>Suggest description</ModalTitle>
          </div>
        </ModalHeader>

        <div className="tw-text-sm tw-font-medium tw-leading-5 tw-text-dark-black">
          What kind of event are you planning?
        </div>

        {/* Commenting out - we are not allowing multiselect for prototype */}
        <div className="tw-text-xs tw-font-normal tw-leading-5 tw-text-primary-grey">
          Select any that apply
        </div>

        <div className="tw-pr-15 tw-flex-row">
          {Object.values(DESCRIPTION_EVENT_TYPE).map((val) => {
            const selected = eventTypes.includes(val);

            return (
              <SelectionButton
                key={val}
                item={val}
                onClick={(): void => {
                  const index = eventTypes?.indexOf(val);

                  if (index === -1) {
                    const temp = [...eventTypes];
                    temp.push(val);
                    setEventTypes(temp);
                  } else {
                    const temp = [...eventTypes];
                    temp.splice(index, 1);

                    setEventTypes(temp);
                  }
                }}
                small
                selected={selected}
              />
            );
          })}
          {eventTypes?.includes(DESCRIPTION_EVENT_TYPE.OTHER) && (
            <>
              <div className="tw-mt-4 tw-text-sm tw-font-medium tw-text-dark-black">Other</div>
              <textarea
                value={other}
                rows={10}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
                  setOther(event.target.value);
                }}
                className="tw-mt-3 tw-max-h-60 tw-w-full tw-rounded-lg tw-border-solid tw-border-border-grey tw-p-4 focus:tw-outline focus:tw-outline-primary-blue"
              />
            </>
          )}
        </div>

        <ModalFooter className="tw-justify-between sm:tw-justify-between">
          <EventDescriptionAISteps step={1} />
          <Button
            variant="primary"
            disabled={!(eventTypes && eventTypes.length > 0)}
            onClick={(): void => {
              handleSubmit(eventTypes, other);
            }}
          >
            Next
            <Icon name="arrow-right" size={16} className="tw-ml-2" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EventTypePromptModal;
